import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  model,
  signal,
  viewChildren,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { ClarityModule } from '@clr/angular';
import { User } from 'src/app/models/api/User';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { SetupConfig } from 'src/app/services/_offline/rep-setup.service';
import { APIService } from 'src/app/services/APIService/api.service';
import { unionBySort } from 'src/app/misc/functions';
import { BegehungPlanNode } from 'src/app/content/begehung/begehung.component';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'src/app/services/toast.service';
import { BegehungApiService } from 'src/app/services/APIService/begehung.api.service';
import { AllSelectedBtnComponent } from '../../_shared/all-selected-btn/all-selected-btn.component';
import { ColumnFilterComponent } from '../../filter/column-filter/column-filter.component';
import { provideFilterStore, FILTER_TABLE_STORE } from 'src/app/shared/stores/filter-table.store';

@Component({
  selector: 'app-replikat-begehungen-setup',
  templateUrl: './replikat-begehungen-setup.component.html',
  styleUrl: './replikat-begehungen-setup.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ColumnFilterComponent,
    AllSelectedBtnComponent,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
  ],
  providers: [provideFilterStore()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReplikatBegehungenSetupComponent {
  protected readonly filterStore = inject(FILTER_TABLE_STORE);
  private readonly api = inject(APIService);
  private readonly begehungApi = inject(BegehungApiService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastService);

  readonly modalOpen = model.required();
  readonly setupConfig = model.required<SetupConfig>();

  private readonly columnFilters = viewChildren<ColumnFilterComponent>('begFilter');

  private readonly usersResource = rxResource({
    params: () => this.modalOpen() || undefined,
    stream: () => this.api.getUsers(),
  });

  protected readonly users = computed(() => this.usersResource.value() ?? { columns: [], rows: [] });
  protected readonly selectedUser = linkedSignal<User>(() => (this.isForSelf(), undefined));
  protected readonly isForSelf = signal(false);

  private readonly begehungenResource = rxResource({
    params: () => (this.isForSelf() ? { id: 0 } : this.selectedUser()),
    stream: ({ params: { id } }) => this.begehungApi.getBegehungsplan(id),
  });

  protected readonly isLoading = computed(() => this.usersResource.isLoading() || this.begehungenResource.isLoading());

  protected readonly begehungen = computed(() => this.begehungenResource.value()?.obege ?? { columns: [], rows: [] });
  protected readonly selectedBegehungen = linkedSignal<BegehungPlanNode[]>(() => (this.selectedUser(), []));
  protected readonly savedSelected = linkedSignal<BegehungPlanNode[]>(() => (this.selectedUser(), []));

  protected replicate() {
    const IDs = this.savedSelected().map(x => x.OBEGEID);
    const objekte = this.begehungenResource.value().sxbeg.filter(x => IDs.includes(x.OBEGEID));

    if (!objekte.length) {
      this.toastr.warning(this.mrTranslate.transform('Keine Objekte sind zugewiesen'));
      return;
    }

    this.setupConfig.set({
      repType: 'Begehung',
      IDs: objekte.map(x => x.OSTAMMID),
      userId: this.selectedUser()?.id,
    });
    this.modalOpen.set(false);
  }

  constructor() {
    this.filterStore.registerTable(this.begehungen);
    this.filterStore.registerFilters(this.columnFilters);

    effect(() => {
      const selected = this.selectedBegehungen();
      if (selected.length) this.savedSelected.update(saved => unionBySort(selected, saved, 'OBEGEID'));
    });
  }

  protected deleteFromSaved(index: number) {
    const row = this.savedSelected()[index];
    this.selectedBegehungen.update(selected => {
      const index = selected.indexOf(row);
      if (index != -1) selected.splice(index, 1);
      return [...selected];
    });
    this.savedSelected.update(saved => (saved.splice(index, 1), [...saved]));
  }
}
